import React from "react";
import { OwcTypography, OwcButton, OwcModalDialog, OwcSelectDropdown, OwcListItem, OwcInput } from "@one/react";
import { Formik } from "formik";
import * as Yup from "yup";
import {
  react18WorkaroundProps,
  THEME,
  isData4eye,
  ReviewersSelect,
  DescriptionItem,
  getShortIdentifier,
  Instrument
} from "@digitallab/grid-common-frontend-components";

const DEFAULT_OTHER_REASON = "Other reason";

export interface ReviewerReasonDialogEquipment {
  inventoryId: string;
  equipmentModel?: string;
  equipmentNickName?: string | null;
  equipmentId?: string;
  serialNumber?: string;
  linkedInstance?: string;
  belongingToGroup?: string;
  siteName?: string;
  entryType?: string;
  gxpRelevant?: string;
  [key: string]: unknown;
}

export interface ReviewerReasonDialogOutput {
  equipmentReviewers: {
    inventoryId: string;
    reviewers: { email: string; userId: string }[];
  }[];
  editReason: string;
  editComment: string;
}

export interface ReviewerReasonDialogProps {
  reasonList: string[];
  equipmentList: ReviewerReasonDialogEquipment[];
  getReviewers: (group: string) => Promise<any[]>;
  onCancel: () => void;
  onSubmit: (values: ReviewerReasonDialogOutput) => void;
  title?: string;
  otherReasonValue?: string;
}

export const ReviewerReasonDialog = ({
  reasonList,
  equipmentList,
  getReviewers,
  onCancel,
  onSubmit,
  title = "Reason for editing equipment",
  otherReasonValue = DEFAULT_OTHER_REASON
}: ReviewerReasonDialogProps) => {
  const fourEPEquipment = equipmentList.filter((item) => isData4eye(item));

  const initialReviewersList = Object.fromEntries(
    fourEPEquipment.map((eq) => [eq.inventoryId, []])
  );

  const isOtherReason = (reason: string) => reason === otherReasonValue;

  const validationSchema = Yup.object({
    reason: Yup.string().required("Reason is required"),
    comment: Yup.string().when("reason", {
      is: (val: string) => isOtherReason(val),
      then: (schema: Yup.StringSchema) =>
        schema.required("Comment is required when reason is 'Other reason'"),
      otherwise: (schema: Yup.StringSchema) => schema.notRequired()
    }),
    reviewersList: Yup.lazy((obj: Record<string, any>) =>
      Yup.object(
        Object.fromEntries(
          Object.keys(obj).map((key) => [
            key,
            Yup.array()
              .min(1, "At least one reviewer is required")
              .required("Reviewer is required")
          ])
        )
      )
    )
  });

  return (
    <OwcModalDialog
      {...react18WorkaroundProps}
      visible
      disableBackdropClick
      size="sm"
      className="owcmodalZIndex"
    >
      <div slot="header">{title}</div>
      <Formik
        initialValues={{
          reason: "",
          comment: "",
          reviewersList: initialReviewersList as Record<string, any[]>
        }}
        validationSchema={validationSchema}
        onSubmit={(values) => {
          const equipmentReviewers = Object.entries(values.reviewersList).map(
            ([inventoryId, reviewers]) => ({
              inventoryId,
              reviewers: reviewers.map((r: any) => ({
                email: r.email,
                userId: r.userId
              }))
            })
          );

          onSubmit({
            equipmentReviewers,
            editReason: values.reason,
            editComment: values.comment
          });
        }}
      >
        {(reviewerFormik) => {
          const hasAllReviewers = fourEPEquipment.every(
            (eq) =>
              (reviewerFormik.values.reviewersList[eq.inventoryId]?.length ?? 0) > 0
          );
          const isCommentRequired =
            isOtherReason(reviewerFormik.values.reason) &&
            !reviewerFormik.values.comment;
          const isSaveDisabled =
            !reviewerFormik.values.reason || !hasAllReviewers || isCommentRequired;

          return (
            <>
              <div
                style={{
                  padding: THEME["one-spacer-16"],
                  paddingBottom: 0,
                  overflow: "auto",
                  maxHeight: "60vh"
                }}
              >
                <div style={{ minHeight: THEME["one-spacer-80"] }}>
                  <OwcSelectDropdown
                    {...react18WorkaroundProps}
                    label="Reason *"
                    style={{
                      backgroundColor: THEME["one-color-cobas-gray-100"],
                      width: "100%"
                    }}
                    onSelectChange={(e: any) => {
                      reviewerFormik.setFieldValue(
                        "reason",
                        reasonList[e.detail?.[0]] || ""
                      );
                    }}
                  >
                    {reasonList.map((reason) => (
                      <OwcListItem
                        {...react18WorkaroundProps}
                        key={reason}
                        value={reason}
                        selected={reason === reviewerFormik.values.reason}
                      >
                        {reason}
                      </OwcListItem>
                    ))}
                  </OwcSelectDropdown>
                </div>
                <div style={{ minHeight: THEME["one-spacer-80"] }}>
                  <OwcInput
                    {...react18WorkaroundProps}
                    label={`Comment${isOtherReason(reviewerFormik.values.reason) ? " *" : ""}`}
                    style={{ width: "100%" }}
                    value={reviewerFormik.values.comment}
                    onInputChange={(e: any) => {
                      reviewerFormik.setFieldValue(
                        "comment",
                        e.target.value || ""
                      );
                    }}
                    maxLength={80}
                    required={isOtherReason(reviewerFormik.values.reason)}
                  />
                </div>
                <OwcTypography
                  {...react18WorkaroundProps}
                  variant="subtitle2"
                >
                  Reviewer required
                </OwcTypography>
                {fourEPEquipment.map((equipment, index) => (
                  <DescriptionItem
                    key={equipment.inventoryId}
                    horizontal
                    keyValue={`reviewer-${index}`}
                    style={{
                      borderBottom: `1px solid ${THEME["one-color-gray-300"]}`,
                      paddingBottom: THEME["one-spacer-4"],
                      paddingTop: THEME["one-spacer-4"]
                    }}
                    label={
                      <div style={{ width: "50%" }}>
                        {getShortIdentifier(equipment as unknown as Instrument) || equipment.inventoryId}
                      </div>
                    }
                    value={
                      <ReviewersSelect
                        name={`reviewersList.${equipment.inventoryId}`}
                        style={{ width: "50%" }}
                        equipmentsGroup={equipment.belongingToGroup}
                        getReviewers={getReviewers}
                      />
                    }
                  />
                ))}
              </div>
              <div
                slot="actions"
                style={{
                  display: "flex",
                  justifyContent: "flex-end",
                  paddingBottom: THEME["one-spacer-16"]
                }}
              >
                <OwcButton
                  {...react18WorkaroundProps}
                  style={{ margin: 2, marginRight: THEME["one-spacer-12"] }}
                  variant="secondary"
                  onClick={onCancel}
                >
                  Cancel
                </OwcButton>
                <OwcButton
                  {...react18WorkaroundProps}
                  style={{ margin: 2 }}
                  disabled={isSaveDisabled}
                  onClick={() => {
                    reviewerFormik.submitForm();
                  }}
                >
                  Save
                </OwcButton>
              </div>
            </>
          );
        }}
      </Formik>
    </OwcModalDialog>
  );
};
